import { useState } from "react";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { feedbackService } from "@project/services/user/feedback.service";
import {
  FEEDBACK_TYPE_OPTIONS,
  type FeedbackType,
} from "@project/types/feedback";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Star } from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

const INITIAL_FORM = {
  full_name: "",
  mobile: "",
  mobileCountry: DEFAULT_PHONE_COUNTRY as PhoneCountryCode,
  email: "",
  feedback_type: "Suggestion" as FeedbackType,
  message: "",
  rating: 5,
};

export default function FeedbackPage() {
  const { t } = useI18n();
  const [f, setF] = useState(INITIAL_FORM);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      f.mobile.trim() &&
      !isValidMobileNumber(f.mobile, f.mobileCountry, { required: false })
    ) {
      projectToast.error(
        "Enter a valid mobile number for the selected country."
      );
      return;
    }
    setBusy(true);
    try {
      await feedbackService.create({
        full_name: f.full_name.trim(),
        mobile: f.mobile.trim() || undefined,
        email: f.email.trim() || undefined,
        feedback_type: f.feedback_type,
        rating: f.rating,
        message: f.message.trim(),
      });
      projectToast.success(t("community.feedback.toast.success"));
      setF(INITIAL_FORM);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, t("community.feedback.toast.error"))
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={t("community.feedback.hero.eyebrow")}
        title={t("community.feedback.hero.title")}
        subtitle={t("community.feedback.hero.subtitle")}
        image={HERO_IMAGES.feedback}
      />
      <form
        onSubmit={submit}
        className="anim-fade-up mx-auto my-12 grid max-w-2xl gap-3 rounded-2xl border border-border bg-card p-6 px-4 py-8 shadow-sm md:px-8"
        style={{ animationDelay: "200ms" }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>{t("community.feedback.form.fullName")}</Label>
            <Input
              required
              value={f.full_name}
              onChange={e => setF({ ...f, full_name: e.target.value })}
            />
          </div>
          <div>
            <Label>{t("community.feedback.form.mobile")}</Label>
            <MobileNumberField
              value={f.mobile}
              country={f.mobileCountry}
              onCountryChange={code => setF({ ...f, mobileCountry: code })}
              onChange={mobile => setF({ ...f, mobile })}
            />
          </div>
        </div>
        <div>
          <Label>{t("community.feedback.form.email")}</Label>
          <Input
            type="email"
            value={f.email}
            onChange={e => setF({ ...f, email: e.target.value })}
          />
        </div>
        <div>
          <Label>{t("community.feedback.form.type")}</Label>
          <select
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={f.feedback_type}
            onChange={e =>
              setF({ ...f, feedback_type: e.target.value as FeedbackType })
            }
          >
            {FEEDBACK_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {t(`community.feedback.types.${option.value.toLowerCase()}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>{t("community.feedback.form.rating")}</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                type="button"
                key={n}
                onClick={() => setF({ ...f, rating: n })}
              >
                <Star
                  className={`h-6 w-6 ${n <= f.rating ? "fill-gold text-gold" : "text-muted-foreground"}`}
                />
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>{t("community.feedback.form.message")}</Label>
          <Textarea
            required
            rows={5}
            value={f.message}
            onChange={e => setF({ ...f, message: e.target.value })}
          />
        </div>
        <Button type="submit" disabled={busy}>
          {busy
            ? t("community.feedback.form.submitting")
            : t("community.feedback.form.submit")}
        </Button>
      </form>
    </PageShell>
  );
}
